/**
 * Kanäle-Reiter: welche Buchungsportale dieses Objekt bedienen und was zuletzt darüber lief.
 *
 * Drei Kennzahlen, darunter eine Karte je Kanal und der Hinweis, wie ein weiteres Portal
 * dazukommt. Die Daten und beide Mutationen liegen in `useChannelsTab`.
 */
import { useState } from 'react';
import { ConfirmDialog, EmptyState } from '@/shared/components';
import type { ChannexChannelResource } from '@/shared/api';
import { isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { ConnectChannelDialog } from '../channel-connections/ConnectChannelDialog';
import { MappingIframeDialog } from '../channel-connections/MappingIframeDialog';
import { AirbnbConnectHint } from '../channels-view/AirbnbConnectHint';
import { canOpenChannelMapping } from '../channels-view/channelMappingAccess';
import { ChannelKpiRow } from '../channels-view/ChannelKpiRow';
import { ChannelMappingBanner } from '../channels-view/ChannelMappingBanner';
import { ChannelsTabHeader } from '../channels-view/ChannelsTabHeader';
import { channelsQueryKey, useChannelsTab } from '../channels-view/useChannelsTab';
import { ChannelRow } from '../channels-view/ChannelRow';
import { ChannelsUnavailable } from '../channels-view/ChannelsUnavailable';
import { getOtaLabel, normalizeChannel } from '../OtaLogo';
import { useApartmentDetail } from './ApartmentDetailLayout';

export function ChannelsTab() {
  const { propertyId: id } = useApartmentDetail();
  const [connectOpen, setConnectOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<ChannexChannelResource | null>(null);
  const { user } = useAuth();
  // Kanäle anlegen, listen und trennen verlangt WorkspaceAdmin (Server:
  // ChannexConnectionsController), der Tab selbst steht auch Staff offen.
  const manages = isWorkspaceAdmin(user);
  const canOpenMapping = canOpenChannelMapping(user);

  const {
    property,
    isLoading,
    channelsKnown,
    channelsError,
    retryChannels,
    ownedChannels,
    activeChannels,
    bookingComConnected,
    airbnbConnected,
    lastBookingByOta,
    mostRecentBooking,
    lastAriPush,
    openMapping,
    disconnect,
  } = useChannelsTab(id, {
    manages,
    onIframeUrl: setIframeUrl,
    onDisconnected: () => setDisconnectTarget(null),
  });

  if (!property) return null;
  const mappingOffered = canOpenMapping && channelsKnown;
  // Ein Ladefehler ohne früheren Stand ist nie „kein Buchungsportal verbunden“.
  const unknown = channelsError !== null && ownedChannels.length === 0;

  return (
    <>
      <ChannelsTabHeader
        channexPropertyId={property.channexPropertyId}
        canOpenMapping={mappingOffered}
        canConnectBookingCom={manages && channelsKnown && !bookingComConnected}
        openingMapping={openMapping.isPending}
        onOpenMapping={() => openMapping.mutate()}
        onConnectBookingCom={() => setConnectOpen(true)}
      />

      {!property.channexPropertyId ? (
        <EmptyState
          icon="Webhook"
          title="Das Objekt ist noch nicht beim Kanalmanager angelegt"
          description="Veröffentlichen Sie zuerst das Objekt. Danach lassen sich hier Buchungsportale verbinden — die Verbindung gilt für alle Apartments des Objekts."
        />
      ) : (
        <>
          <ChannelKpiRow
            activeChannelCount={unknown ? null : activeChannels.length}
            lastPushAt={lastAriPush?.occurredAt}
            lastBookingAt={mostRecentBooking?.receivedAt}
          />

          {channelsError && (
            <ChannelsUnavailable error={channelsError} stale={!unknown} onRetry={retryChannels} />
          )}
          {isLoading ? (
            <p className="text-ink-soft text-sm" role="status">
              Kanäle werden geladen…
            </p>
          ) : unknown ? null : ownedChannels.length === 0 ? (
            <div className="bg-card border-line/50 shadow-card space-y-3 rounded-lg border p-6 text-sm">
              <p className="text-ink-soft">
                {manages
                  ? 'Noch kein Buchungsportal verbunden. Über „Booking.com verbinden" tragen Sie die Objektkennung aus dem Booking.com-Extranet ein.'
                  : 'Noch kein Buchungsportal verbunden. Das Verbinden übernimmt die Workspace-Verwaltung.'}
              </p>
              {mappingOffered && <AirbnbConnectHint onOpenIframe={() => openMapping.mutate()} />}
            </div>
          ) : (
            <ul className="flex flex-col gap-4">
              {ownedChannels.map((c) => (
                <ChannelRow
                  key={c.id}
                  channel={c}
                  lastBooking={lastBookingByOta.get(normalizeChannel(c.attributes.channel)) ?? null}
                  // Ein Klick nimmt den Kanal für JEDES Apartment des Objekts vom Netz und löscht
                  // ihn beim Vendor: deshalb die Rückfrage mit dem Kanalnamen im eigenen Dialog.
                  onDisconnect={() => setDisconnectTarget(c)}
                  disconnecting={disconnect.isPending && disconnectTarget?.id === c.id}
                />
              ))}
              {mappingOffered && !airbnbConnected && (
                <AirbnbConnectHint onOpenIframe={() => openMapping.mutate()} />
              )}
            </ul>
          )}

          {mappingOffered && <ChannelMappingBanner onOpenMapping={() => openMapping.mutate()} />}
        </>
      )}

      <ConfirmDialog
        open={disconnectTarget !== null}
        onOpenChange={(o) => !o && setDisconnectTarget(null)}
        title={`${disconnectTarget ? getOtaLabel(disconnectTarget.attributes.channel) : ''} trennen?`}
        description="Die Verbindung wird für alle Apartments dieses Objekts aufgehoben und beim Kanalmanager gelöscht. Bestehende Buchungen bleiben erhalten, neue kommen über dieses Portal nicht mehr an."
        confirmLabel="Verbindung trennen"
        loading={disconnect.isPending}
        onConfirm={() => {
          if (disconnectTarget) disconnect.mutate(disconnectTarget.id);
        }}
      />

      <MappingIframeDialog iframeUrl={iframeUrl} onOpenChange={(o) => !o && setIframeUrl(null)} />
      {property.channexPropertyId && manages && (
        <ConnectChannelDialog
          open={connectOpen}
          onOpenChange={setConnectOpen}
          channexPropertyId={property.channexPropertyId}
          currency={property.currency}
          channelsQueryKey={channelsQueryKey}
          canOpenMapping={canOpenMapping}
        />
      )}
    </>
  );
}
